import { useEffect, useRef, useState } from "react";
import bellIcon from "../bell-ring.png";
import {
  groupNotifications,
  shortNotificationText,
  timeAgo,
} from "../utils/parsers";

// Header bell: unread badge, and a dropdown listing the notifications the
// algorithm and the backend wrote (processed with its duration, stopped,
// failed, re-queued). Opening it marks everything read; the rows that were
// unread at that moment stay highlighted until the panel closes.
export default function BellMenu({ items, unread, status, error, onOpen }) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(() => new Set());
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const toggle = () => {
    if (open) {
      setOpen(false);
      return;
    }
    setHighlight(new Set(items.filter((item) => !item.isRead).map((item) => item.id)));
    setOpen(true);
    if (onOpen) onOpen();
  };

  const entries = groupNotifications(items);
  const badge = unread > 99 ? "99+" : String(unread);

  return (
    <div className="bell" ref={ref}>
      <button
        type="button"
        className="bell-btn"
        aria-label={unread > 0 ? `Notifications (${unread} unread)` : "Notifications"}
        aria-expanded={open}
        onClick={toggle}
      >
        <img src={bellIcon} alt="" />
        {unread > 0 && (
          <span className="bell-badge" data-testid="bell-badge">
            {badge}
          </span>
        )}
      </button>

      {open && (
        <div className="bell-panel" role="dialog" aria-label="Notifications">
          <div className="bell-head">Notifications</div>
          {status === "error" && (
            <p className="status-text error">Could not load notifications: {error}</p>
          )}
          {status !== "error" && entries.length === 0 && (
            <p className="bell-empty">
              {status === "loading" ? "Loading..." : "No notifications yet."}
            </p>
          )}
          <ul className="bell-list">
            {entries.map((entry) => {
              const isNew = entry.items.some((item) => highlight.has(item.id));
              return (
                <li
                  key={entry.key}
                  className={`bell-item ${entry.kind}${isNew ? " unread" : ""}`}
                >
                  <div className="bell-item-title">
                    {entry.fileName ?? entry.items[0].message}
                  </div>
                  {entry.fileName &&
                    entry.items.map((item) => (
                      <div className="bell-item-line" key={item.id}>
                        {item.assetType && <span className="chip">{item.assetType}</span>}
                        <span>{shortNotificationText(item)}</span>
                      </div>
                    ))}
                  <div className="bell-item-time">{timeAgo(entry.latest)}</div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
